import type { StateCreator, StoreApi } from '@bomdisoft/store/vanilla';

type Selector<T, U> = (state: T) => U;

/**
 * Adds the selective `subscribe(selector, listener, options?)` overload from
 * `zustand/middleware`'s `subscribeWithSelector` alongside the plain `subscribe(listener)`.
 */
export const subscribeWithSelector =
  <T>(config: StateCreator<T>): StateCreator<T> =>
  (set, get, api) => {
    const origSubscribe = api.subscribe as (listener: (state: T, prev: T) => void) => () => void;
    api.subscribe = ((selector: Selector<T, unknown>, optListener?: (a: unknown, b: unknown) => void, options?: { equalityFn?: (a: unknown, b: unknown) => boolean; fireImmediately?: boolean }) => {
      if (!optListener) return origSubscribe(selector);
      const equalityFn = options?.equalityFn ?? Object.is;
      let currentSlice = selector(api.getState());
      if (options?.fireImmediately) optListener(currentSlice, currentSlice);
      return origSubscribe(state => {
        const nextSlice = selector(state);
        if (!equalityFn(currentSlice, nextSlice)) {
          const previousSlice = currentSlice;
          optListener((currentSlice = nextSlice), previousSlice);
        }
      });
    }) as StoreApi<T>['subscribe'];
    return config(set, get, api);
  };

/**
 * No-op passthrough. The redux-devtools bridge is dev-only third-party surface; the
 * store works identically without it. Signature-compatible so composition is unchanged.
 */
export const devtools =
  <T>(config: StateCreator<T>, _options?: { name?: string; enabled?: boolean; store?: string }): StateCreator<T> =>
  (set, get, api) =>
    config(set, get, api);

type StateStorage = {
  getItem: (name: string) => string | null;
  setItem: (name: string, value: string) => void;
  removeItem: (name: string) => void;
};

export type PersistOptions<T, P = T> = {
  name: string;
  version?: number;
  storage?: StateStorage;
  partialize?: (state: T) => P;
  merge?: (persisted: unknown, current: T) => T;
  migrate?: (persistedState: unknown, version: number) => T | P;
};

const defaultStorage = (): StateStorage | undefined => (typeof localStorage === 'undefined' ? undefined : localStorage);

/**
 * localStorage persistence. Mirrors `zustand/middleware`'s `persist`: writes
 * `{ state, version }` on every change, rehydrates synchronously on creation, and
 * discards a persisted blob whose version doesn't match (unless a `migrate` is given).
 */
export const persist =
  <T>(config: StateCreator<T>, options: PersistOptions<T>): StateCreator<T> =>
  (set, get, api) => {
    const version = options.version ?? 0;
    const storage = options.storage ?? defaultStorage();
    const merge = options.merge ?? ((persisted, current) => Object.assign({}, current, persisted as Partial<T>));

    const state = config(set, get, api);

    if (storage) {
      try {
        const stored = storage.getItem(options.name);
        if (stored) {
          const parsed = JSON.parse(stored) as { state: unknown; version?: number };
          const persisted = parsed.version === version ? parsed.state : options.migrate ? options.migrate(parsed.state, parsed.version ?? 0) : undefined;
          if (persisted !== undefined) Object.assign(state as object, merge(persisted, state));
        }
      } catch (error) {
        console.warn(`store persist: "${options.name}" could not be read, starting from the initial state`, error);
      }
      api.subscribe(current => {
        try {
          storage.setItem(options.name, JSON.stringify({ state: options.partialize ? options.partialize(current) : current, version }));
        } catch (error) {
          console.warn(`store persist: "${options.name}" could not be written, this change stays in memory`, error);
        }
      });
    }

    return state;
  };
